/** @category Validation Rules */

import { didYouMean } from '../../jsutils/didYouMean.ts';
import { suggestionList } from '../../jsutils/suggestionList.ts';

import { GraphQLError } from '../../error/GraphQLError.ts';

import { Kind } from '../../language/kinds.ts';
import type { ASTVisitor } from '../../language/visitor.ts';

import { specifiedDirectives } from '../../type/directives.ts';

import type {
  SDLValidationContext,
  ValidationContext,
} from '../ValidationContext.ts';

/**
 * Known argument names
 *
 * A GraphQL field is only valid if all supplied arguments are defined by
 * that field.
 *
 * See https://spec.graphql.org/draft/#sec-Argument-Names
 * See https://spec.graphql.org/draft/#sec-Directives-Are-In-Valid-Locations
 * @param context - The validation context used while checking the document.
 * @returns A visitor that reports validation errors for this rule.
 * @example
 * ```ts
 * import { buildSchema, parse, validate } from 'graphql';
 * import { KnownArgumentNamesRule } from 'graphql/validation';
 *
 * const schema = buildSchema(`
 *   type Query {
 *     field(arg: String): String
 *   }
 * `);
 *
 * const invalidDocument = parse(`
 *   { field(unknown: "1") }
 * `);
 * const invalidErrors = validate(schema, invalidDocument, [
 *   KnownArgumentNamesRule,
 * ]);
 *
 * invalidErrors.length; // => 1
 *
 * const validDocument = parse(`
 *   { field(arg: "1") }
 * `);
 * const validErrors = validate(schema, validDocument, [KnownArgumentNamesRule]);
 *
 * validErrors; // => []
 * ```
 */
export function KnownArgumentNamesRule(context: ValidationContext): ASTVisitor {
  return {
    // eslint-disable-next-line new-cap
    ...KnownArgumentNamesOnDirectivesRule(context),
    FragmentArgument(argNode) {
      const fragmentSignature = context.getFragmentSignature();
      if (fragmentSignature) {
        const varDef = fragmentSignature.variableDefinitions.get(
          argNode.name.value,
        );
        if (!varDef) {
          const argName = argNode.name.value;
          const suggestions = context.hideSuggestions
            ? []
            : suggestionList(
                argName,
                Array.from(fragmentSignature.variableDefinitions.values()).map(
                  (varSignature) => varSignature.variable.name.value,
                ),
              );
          context.reportError(
            new GraphQLError(
              `Unknown argument "${argName}" on fragment "${fragmentSignature.definition.name.value}".` +
                didYouMean(suggestions),
              { nodes: argNode },
            ),
          );
        }
      }
    },
    Argument(argNode) {
      const argDef = context.getArgument();
      const fieldDef = context.getFieldDef();

      if (!argDef && fieldDef) {
        const argName = argNode.name.value;
        const suggestions = context.hideSuggestions
          ? []
          : suggestionList(
              argName,
              fieldDef.args.map((arg) => arg.name),
            );
        context.reportError(
          new GraphQLError(
            `Unknown argument "${argName}" on field "${fieldDef}".` +
              didYouMean(suggestions),
            { nodes: argNode },
          ),
        );
      }
    },
  };
}

/** @internal */
export function KnownArgumentNamesOnDirectivesRule(
  context: ValidationContext | SDLValidationContext,
): ASTVisitor {
  const directiveArgs = new Map<string, ReadonlyArray<string>>();

  const schema = context.getSchema();
  const definedDirectives = schema
    ? schema.getDirectives()
    : specifiedDirectives;
  for (const directive of definedDirectives) {
    directiveArgs.set(
      directive.name,
      directive.args.map((arg) => arg.name),
    );
  }

  const astDefinitions = context.getDocument().definitions;
  for (const def of astDefinitions) {
    if (def.kind === Kind.DIRECTIVE_DEFINITION) {
      const argsNodes = def.arguments ?? [];

      directiveArgs.set(
        def.name.value,
        argsNodes.map((arg) => arg.name.value),
      );
    }
  }

  return {
    Directive(directiveNode) {
      const directiveName = directiveNode.name.value;
      const knownArgs = directiveArgs.get(directiveName);

      if (directiveNode.arguments != null && knownArgs != null) {
        for (const argNode of directiveNode.arguments) {
          const argName = argNode.name.value;
          if (!knownArgs.includes(argName)) {
            const suggestions = suggestionList(argName, knownArgs);
            context.reportError(
              new GraphQLError(
                `Unknown argument "${argName}" on directive "@${directiveName}".` +
                  (context.hideSuggestions ? '' : didYouMean(suggestions)),
                { nodes: argNode },
              ),
            );
          }
        }
      }

      return false;
    },
  };
}
